import React, { useCallback, useEffect, useState } from 'react';
import { Channel, CreateChannelRequest, UpdateChannelRequest } from './types';
import { channelsApi, aiApi, emailApi, oauthApi } from './services/communicationsApi';
import { useChannels } from './hooks/useChannels';
import { useEmailMessages } from './hooks/useEmailMessages';
import { useEmailMailboxes } from './hooks/useEmailMailboxes';
import { useAiMessages } from './hooks/useAiMessages';
import { useAiSmartFolders } from './hooks/useAiSmartFolders';
import ChannelNav from './ChannelNav';
import ChannelSettingsForm from './ChannelSettingsForm';
import AiChannelSettingsForm from './ai/AiChannelSettingsForm';
import EmailMessageList from './email/EmailMessageList';
import EmailMessageView from './email/EmailMessageView';
import EmailComposeComposer from './email/EmailComposeComposer';
import EmailMoveDialog from './email/EmailMoveDialog';
import { EmailMessageAction } from './email/EmailMessageActionsTab';
import { EmailComposeMode, EmailMessageSummary, EmailReplyDraft, EmailMailbox, AiSmartFolder } from './types';
import {
  apiErrorMessage,
  isOAuthAuthorizationRequiredError,
  needsOAuthAuthorization,
  oauthProviderForAuthorize,
  setOAuthReturnIntent,
  takeOAuthReturnIntent,
  type OAuthReturnIntent
} from './utils/emailChannel';
import './Communications.css';

type ContentView = 'channel' | 'add-channel' | 'edit-channel' | 'none';

const DEFAULT_MAILBOX = 'INBOX';
const DEFAULT_AI_FOLDER = 'focus';

export interface OAuthReturn {
  status: 'success' | 'error';
  channelId?: number;
  reason?: string;
}

interface CommunicationsAppProps {
  oauthReturn?: OAuthReturn | null;
  onDirtyChange?: (dirty: boolean) => void;
  isActive?: boolean;
}

function pickDefaultMailbox(
  mailboxes: { path: string; specialUse?: string }[],
  channel?: Channel | null
): string {
  const configuredInbox = channel?.inboxMailbox?.trim();
  if (configuredInbox && mailboxes.some(m => m.path === configuredInbox)) {
    return configuredInbox;
  }
  const inbox = mailboxes.find(
    m => m.specialUse === '\\Inbox' || m.path.toUpperCase() === (configuredInbox || 'INBOX').toUpperCase()
  );
  return inbox?.path ?? mailboxes[0]?.path ?? configuredInbox ?? DEFAULT_MAILBOX;
}

const CommunicationsApp: React.FC<CommunicationsAppProps> = ({ oauthReturn, onDirtyChange, isActive = true }) => {
  const { channels, loading: channelsLoading, error: channelsError, fetchChannels, createChannel, updateChannel, deleteChannel } = useChannels({ enabled: isActive });
  const { messages, selectedMessage, loading, syncing, messageLoading, error: messagesError, fetchMessages, syncMessages, fetchMessage, clearSelectedMessage, clearMessages: clearEmailMessages, removeMessageFromList, updateMessageReadState } = useEmailMessages();
  const { mailboxes, loading: mailboxesLoading, error: mailboxesError, fetchMailboxes } = useEmailMailboxes();
  const {
    messages: aiMessages,
    selectedMessage: aiSelectedMessage,
    loading: aiLoading,
    syncing: aiSyncing,
    messageLoading: aiMessageLoading,
    error: aiMessagesError,
    fetchMessages: fetchAiMessages,
    syncMessages: syncAiMessages,
    fetchMessage: fetchAiMessage,
    clearSelectedMessage: clearAiSelectedMessage,
    clearMessages: clearAiMessages,
    removeMessageFromList: removeAiMessageFromList,
    updateMessageReadState: updateAiMessageReadState
  } = useAiMessages();
  const { loading: smartFoldersLoading, error: smartFoldersError, fetchSmartFolders } = useAiSmartFolders();

  const [selectedChannel, setSelectedChannel] = React.useState<Channel | null>(null);
  const [contentView, setContentView] = React.useState<ContentView>('none');
  const [oauthMessage, setOauthMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedMailbox, setSelectedMailbox] = useState(DEFAULT_MAILBOX);
  const [selectedFolder, setSelectedFolder] = useState(DEFAULT_AI_FOLDER);
  const [selectedListMessage, setSelectedListMessage] = useState<{ uid: number; mailbox: string } | null>(null);

  const clearSelectedListMessage = useCallback(() => {
    setSelectedListMessage(null);
  }, []);

  const [expandedChannelIds, setExpandedChannelIds] = useState<Set<number>>(() => new Set());
  const [mailboxesByChannelId, setMailboxesByChannelId] = useState<Record<number, EmailMailbox[]>>({});
  const [smartFoldersByChannelId, setSmartFoldersByChannelId] = useState<Record<number, AiSmartFolder[]>>({});
  const [originMailboxes, setOriginMailboxes] = useState<EmailMailbox[]>([]);
  const [originMailboxesLoading, setOriginMailboxesLoading] = useState(false);
  const [originMailboxesError, setOriginMailboxesError] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState<EmailComposeMode>('reply');
  const [composeDraft, setComposeDraft] = useState<EmailReplyDraft | null>(null);
  const [composeDraftLoading, setComposeDraftLoading] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<EmailMessageSummary | null>(null);
  const lastSyncAtRef = React.useRef<number | null>(null);
  const autoSyncInFlightRef = React.useRef(false);
  const oauthRedirectInFlightRef = React.useRef(false);

  const beginChannelOAuth = useCallback(async (channel: Channel, intent: OAuthReturnIntent) => {
    const provider = oauthProviderForAuthorize(channel);
    if (!provider || oauthRedirectInFlightRef.current) return false;
    oauthRedirectInFlightRef.current = true;
    setOAuthReturnIntent(intent);
    try {
      const { url } = await oauthApi.getAuthorizeUrl(channel.id, provider);
      window.location.href = url;
      return true;
    } catch (err) {
      oauthRedirectInFlightRef.current = false;
      setOauthMessage({
        type: 'error',
        text: apiErrorMessage(err, 'Failed to start authorization.')
      });
      setSelectedChannel(channel);
      setContentView('edit-channel');
      return false;
    }
  }, []);

  const isEmailChannel = selectedChannel?.type === 'email';
  const isAiChannel = selectedChannel?.type === 'ai';
  const activeMessages = isAiChannel ? aiMessages : messages;
  const activeSelectedMessage = isAiChannel ? aiSelectedMessage : selectedMessage;
  const activeLoading = isAiChannel ? aiLoading : loading;
  const activeSyncing = isAiChannel ? aiSyncing : syncing;
  const activeMessageLoading = isAiChannel ? aiMessageLoading : messageLoading;
  const activeMessagesError = isAiChannel ? aiMessagesError : messagesError;
  const activeListKey = isAiChannel ? selectedFolder : selectedMailbox;
  const activeListLabel = isAiChannel
    ? (smartFoldersByChannelId[selectedChannel?.id ?? -1]?.find(f => f.id === selectedFolder)?.name ?? selectedFolder)
    : (mailboxesByChannelId[selectedChannel?.id ?? -1]?.find(m => m.path === selectedMailbox)?.name ?? selectedMailbox);

  const runSync = useCallback(async (channelId: number, listKey: string) => {
    if ((isAiChannel ? aiSyncing : syncing) || autoSyncInFlightRef.current) return;

    autoSyncInFlightRef.current = true;
    try {
      if (isAiChannel) {
        await syncAiMessages(channelId, listKey);
      } else {
        await syncMessages(channelId, listKey);
      }
      lastSyncAtRef.current = Date.now();
    } catch {
      // error surfaced via messagesError
    } finally {
      autoSyncInFlightRef.current = false;
    }
  }, [isAiChannel, syncMessages, syncAiMessages, syncing, aiSyncing]);

  const runBackgroundSync = useCallback((channelId: number, mailbox: string) => {
    if (!isActive) return;
    const last = lastSyncAtRef.current;
    if (last !== null && Date.now() - last < 30_000) return;
    void runSync(channelId, mailbox);
  }, [runSync, isActive]);

  const loadEmailChannel = useCallback(async (channel: Channel, mailbox?: string) => {
    if (needsOAuthAuthorization(channel)) {
      await beginChannelOAuth(channel, 'channel');
      return;
    }
    try {
      let boxes = mailboxesByChannelId[channel.id];
      if (!boxes?.length) {
        boxes = await fetchMailboxes(channel.id);
        setMailboxesByChannelId(prev => ({ ...prev, [channel.id]: boxes }));
      }
      const target = mailbox && boxes.some(b => b.path === mailbox)
        ? mailbox
        : pickDefaultMailbox(boxes, channel);
      setSelectedMailbox(target);
      await fetchMessages(channel.id, target);
      runBackgroundSync(channel.id, target);
    } catch (err) {
      const message = apiErrorMessage(err);
      // Tokens revoked or expired — start the same OAuth flow as channel settings.
      if (isOAuthAuthorizationRequiredError(message) && oauthProviderForAuthorize(channel)) {
        await beginChannelOAuth(channel, 'channel');
        return;
      }
      throw err;
    }
  }, [fetchMailboxes, fetchMessages, runBackgroundSync, mailboxesByChannelId, beginChannelOAuth]);

  useEffect(() => {
    if (!oauthReturn) return;

    const handleOAuthReturn = async () => {
      const returnIntent = takeOAuthReturnIntent();
      if (oauthReturn.status === 'success' && oauthReturn.channelId) {
        await fetchChannels();
        try {
          const ch = await channelsApi.getById(oauthReturn.channelId);
          setSelectedChannel(ch);
          const connectedText = ch.oauthEmail
            ? `Connected as ${ch.oauthEmail}.`
            : ch.oauthProvider === 'microsoft'
              ? 'Microsoft account connected.'
              : 'Google account connected.';

          // Channel selection: authorize → then load inbox.
          // Settings Authorize: return to Edit Configuration.
          if (returnIntent === 'channel') {
            setExpandedChannelIds(prev => new Set(prev).add(ch.id));
            if (needsOAuthAuthorization(ch)) {
              setContentView('edit-channel');
              setOauthMessage({ type: 'error', text: 'Authorization did not complete. Authorize from channel settings.' });
              return;
            }
            setOauthMessage(null);
            setContentView('channel');
            try {
              await loadEmailChannel(ch);
            } catch {
              // errors surfaced via mailboxesError / messagesError
            }
            return;
          }

          setOauthMessage({ type: 'success', text: connectedText });
          setContentView('edit-channel');
        } catch {
          setOauthMessage({ type: 'success', text: 'Account connected.' });
          setContentView(returnIntent === 'channel' ? 'channel' : 'edit-channel');
        }
      } else if (oauthReturn.status === 'error') {
        setOauthMessage({ type: 'error', text: oauthReturn.reason || 'Authorization failed.' });
        if (oauthReturn.channelId) {
          try {
            const ch = await channelsApi.getById(oauthReturn.channelId);
            setSelectedChannel(ch);
            setContentView('edit-channel');
          } catch {
            // channel not found — show error on welcome view
          }
        }
      }
    };

    handleOAuthReturn();
    // Intentionally omit loadEmailChannel: run once per oauthReturn payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthReturn, fetchChannels]);

  const loadAiChannel = useCallback(async (channel: Channel, folder?: string) => {
    let folders = smartFoldersByChannelId[channel.id];
    if (!folders?.length) {
      folders = await fetchSmartFolders(channel.id);
      setSmartFoldersByChannelId(prev => ({ ...prev, [channel.id]: folders }));
    }
    const target = folder || DEFAULT_AI_FOLDER;
    setSelectedFolder(target);
    await fetchAiMessages(channel.id, target);
  }, [fetchSmartFolders, fetchAiMessages, smartFoldersByChannelId]);

  const closeCompose = useCallback(() => {
    setComposeOpen(false);
    setComposeDraft(null);
    setComposeError(null);
    setComposeDraftLoading(false);
    setComposeSending(false);
    setActionMessage(null);
  }, []);

  const closeMoveDialog = useCallback(() => {
    setMoveOpen(false);
    setMoveError(null);
    setActionMessage(null);
  }, []);

  const handleSelectChannel = useCallback(async (channel: Channel) => {
    const isSameChannel = selectedChannel?.id === channel.id;
    const isExpanded = expandedChannelIds.has(channel.id);

    if (channel.type === 'email' && needsOAuthAuthorization(channel)) {
      setSelectedChannel(channel);
      setExpandedChannelIds(prev => new Set(prev).add(channel.id));
      await beginChannelOAuth(channel, 'channel');
      return;
    }

    if (isSameChannel && contentView === 'channel' && isExpanded) {
      return;
    }

    if (isSameChannel && contentView === 'channel' && !isExpanded) {
      setExpandedChannelIds(prev => new Set(prev).add(channel.id));
      return;
    }

    setSelectedChannel(channel);
    setContentView('channel');
    setExpandedChannelIds(prev => new Set(prev).add(channel.id));
    closeCompose();
    closeMoveDialog();
    clearSelectedMessage();
    clearAiSelectedMessage();
    clearSelectedListMessage();
    clearEmailMessages();
    clearAiMessages();
    setOriginMailboxes([]);
    lastSyncAtRef.current = null;
    if (channel.type === 'email') {
      try {
        await loadEmailChannel(channel);
      } catch {
        // errors surfaced via mailboxesError / messagesError
      }
    } else if (channel.type === 'ai') {
      try {
        await loadAiChannel(channel);
      } catch {
        // errors surfaced via smartFoldersError / aiMessagesError
      }
    }
  }, [
    selectedChannel,
    expandedChannelIds,
    contentView,
    loadEmailChannel,
    loadAiChannel,
    beginChannelOAuth,
    clearSelectedMessage,
    clearAiSelectedMessage,
    clearSelectedListMessage,
    clearEmailMessages,
    clearAiMessages,
    closeCompose,
    closeMoveDialog
  ]);

  const handleAddChannel = useCallback(() => {
    setSelectedChannel(null);
    setContentView('add-channel');
  }, []);

  const handleEditChannel = useCallback((channel: Channel) => {
    setSelectedChannel(channel);
    setContentView('edit-channel');
  }, []);

  const handleDeleteChannel = useCallback(async (channel: Channel) => {
    await deleteChannel(channel.id);
    setExpandedChannelIds(prev => {
      if (!prev.has(channel.id)) return prev;
      const next = new Set(prev);
      next.delete(channel.id);
      return next;
    });
    setMailboxesByChannelId(prev => {
      if (!(channel.id in prev)) return prev;
      const next = { ...prev };
      delete next[channel.id];
      return next;
    });
    setSmartFoldersByChannelId(prev => {
      if (!(channel.id in prev)) return prev;
      const next = { ...prev };
      delete next[channel.id];
      return next;
    });
    if (selectedChannel?.id === channel.id) {
      setSelectedChannel(null);
      setContentView('none');
      clearSelectedMessage();
      clearAiSelectedMessage();
      clearSelectedListMessage();
    }
  }, [deleteChannel, selectedChannel, clearSelectedMessage, clearAiSelectedMessage, clearSelectedListMessage]);

  const activateChannelView = useCallback((channel: Channel) => {
    setSelectedChannel(channel);
    setContentView('channel');
    setExpandedChannelIds(prev => new Set(prev).add(channel.id));
    closeCompose();
    closeMoveDialog();
    clearSelectedMessage();
    clearAiSelectedMessage();
    clearSelectedListMessage();
    clearEmailMessages();
    clearAiMessages();
    setOriginMailboxes([]);
    lastSyncAtRef.current = null;
  }, [
    closeCompose,
    closeMoveDialog,
    clearSelectedMessage,
    clearAiSelectedMessage,
    clearSelectedListMessage,
    clearEmailMessages,
    clearAiMessages
  ]);

  const handleSelectChannelMailbox = useCallback(async (channel: Channel, mailbox: string) => {
    const switching = selectedChannel?.id !== channel.id || contentView !== 'channel';
    if (switching) {
      activateChannelView(channel);
    }
    if (mailbox === selectedMailbox && !switching) return;

    let boxes = mailboxesByChannelId[channel.id];
    if (!boxes?.length) {
      boxes = await fetchMailboxes(channel.id);
      setMailboxesByChannelId(prev => ({ ...prev, [channel.id]: boxes }));
    }

    setSelectedMailbox(mailbox);
    closeCompose();
    clearSelectedMessage();
    clearSelectedListMessage();
    await fetchMessages(channel.id, mailbox);
    lastSyncAtRef.current = null;
    runBackgroundSync(channel.id, mailbox);
  }, [
    selectedChannel,
    contentView,
    selectedMailbox,
    mailboxesByChannelId,
    activateChannelView,
    fetchMailboxes,
    fetchMessages,
    runBackgroundSync,
    closeCompose,
    clearSelectedMessage,
    clearSelectedListMessage
  ]);

  const handleSelectChannelFolder = useCallback(async (channel: Channel, folder: string) => {
    const switching = selectedChannel?.id !== channel.id || contentView !== 'channel';
    if (switching) {
      activateChannelView(channel);
    }
    if (folder === selectedFolder && !switching) return;

    let folders = smartFoldersByChannelId[channel.id];
    if (!folders?.length) {
      folders = await fetchSmartFolders(channel.id);
      setSmartFoldersByChannelId(prev => ({ ...prev, [channel.id]: folders }));
    }

    setSelectedFolder(folder);
    closeCompose();
    clearAiSelectedMessage();
    clearSelectedListMessage();
    await fetchAiMessages(channel.id, folder);
  }, [
    selectedChannel,
    contentView,
    selectedFolder,
    smartFoldersByChannelId,
    activateChannelView,
    fetchSmartFolders,
    fetchAiMessages,
    closeCompose,
    clearAiSelectedMessage,
    clearSelectedListMessage
  ]);

  const refreshSelectedChannel = useCallback(async () => {
    if (!selectedChannel?.id) return;
    const ch = await channelsApi.getById(selectedChannel.id);
    setSelectedChannel(ch);
    await fetchChannels();
  }, [selectedChannel?.id, fetchChannels]);

  const handleSaveChannel = useCallback(async (data: CreateChannelRequest | UpdateChannelRequest) => {
    const stayOnSettingsForOAuth = (ch: Channel) =>
      ch.type === 'email' && needsOAuthAuthorization(ch);

    if ('id' in data && data.id) {
      const updated = await updateChannel(data.id, data as UpdateChannelRequest);
      setSelectedChannel(updated);
      setContentView(stayOnSettingsForOAuth(updated) ? 'edit-channel' : 'channel');
      setExpandedChannelIds(prev => new Set(prev).add(updated.id));
      if (!stayOnSettingsForOAuth(updated)) {
        if (updated.type === 'email') {
          await loadEmailChannel(updated);
        } else if (updated.type === 'ai') {
          await loadAiChannel(updated);
        }
      }
    } else {
      const created = await createChannel(data as CreateChannelRequest);
      setSelectedChannel(created);
      setContentView(stayOnSettingsForOAuth(created) ? 'edit-channel' : 'channel');
      setExpandedChannelIds(prev => new Set(prev).add(created.id));
      if (!stayOnSettingsForOAuth(created)) {
        if (created.type === 'email') {
          await loadEmailChannel(created);
        } else if (created.type === 'ai') {
          await loadAiChannel(created);
        }
      }
    }
  }, [createChannel, updateChannel, loadEmailChannel, loadAiChannel]);

  const handleCancelChannelForm = useCallback(() => {
    if (selectedChannel) {
      setContentView('channel');
    } else {
      setContentView('none');
    }
  }, [selectedChannel]);

  const handleCollapseChannel = useCallback((channelId: number) => {
    setExpandedChannelIds(prev => {
      if (!prev.has(channelId)) return prev;
      const next = new Set(prev);
      next.delete(channelId);
      return next;
    });
  }, []);

  const handleSyncMessages = useCallback(() => {
    if (!selectedChannel) return;
    lastSyncAtRef.current = null;
    void runSync(selectedChannel.id, activeListKey);
  }, [selectedChannel, activeListKey, runSync]);

  useEffect(() => {
    if (selectedChannel?.type !== 'email') return;
    lastSyncAtRef.current = null;
  }, [selectedChannel?.id, selectedMailbox]);

  useEffect(() => {
    if (!isActive || contentView !== 'channel' || !selectedChannel || selectedChannel.type !== 'email') return;

    const maybeAutoSync = () => {
      const last = lastSyncAtRef.current;
      if (last !== null && Date.now() - last < 30_000) return;
      runBackgroundSync(selectedChannel.id, selectedMailbox);
    };

    const intervalId = window.setInterval(maybeAutoSync, 5_000);
    return () => window.clearInterval(intervalId);
  }, [isActive, contentView, selectedChannel, selectedMailbox, runBackgroundSync]);

  const handleSelectMessage = useCallback((msg: EmailMessageSummary) => {
    if (!selectedChannel) return;
    setSelectedListMessage({ uid: msg.uid, mailbox: msg.mailbox });
    closeCompose();
    closeMoveDialog();
    if (selectedChannel.type === 'ai') {
      void fetchAiMessage(selectedChannel.id, msg.mailbox, msg.uid, msg);
    } else {
      void fetchMessage(selectedChannel.id, msg.mailbox, msg.uid, msg);
    }
  }, [selectedChannel, fetchMessage, fetchAiMessage, closeCompose, closeMoveDialog]);

  const ensureActionMessageLoaded = useCallback(async (msg: EmailMessageSummary) => {
    if (!selectedChannel) return;
    if (activeSelectedMessage?.uid !== msg.uid || activeSelectedMessage?.mailbox !== msg.mailbox) {
      if (selectedChannel.type === 'ai') {
        await fetchAiMessage(selectedChannel.id, msg.mailbox, msg.uid, msg);
      } else {
        await fetchMessage(selectedChannel.id, msg.mailbox, msg.uid, msg);
      }
    }
  }, [selectedChannel, activeSelectedMessage, fetchMessage, fetchAiMessage]);

  const loadComposeDraft = useCallback(async (mode: EmailComposeMode, msg: EmailMessageSummary) => {
    if (!selectedChannel) return;
    setComposeMode(mode);
    setComposeOpen(true);
    setComposeDraft(null);
    setComposeError(null);
    setComposeDraftLoading(true);
    setActionMessage(msg);
    closeMoveDialog();

    try {
      await ensureActionMessageLoaded(msg);
      const draft = mode === 'reply-all'
        ? isAiChannel
          ? await aiApi.getReplyAllDraft(selectedChannel.id, msg.uid)
          : await emailApi.getReplyAllDraft(selectedChannel.id, msg.mailbox, msg.uid)
        : mode === 'forward'
          ? isAiChannel
            ? await aiApi.getForwardDraft(selectedChannel.id, msg.uid)
            : await emailApi.getForwardDraft(selectedChannel.id, msg.mailbox, msg.uid)
          : isAiChannel
            ? await aiApi.getReplyDraft(selectedChannel.id, msg.uid)
            : await emailApi.getReplyDraft(selectedChannel.id, msg.mailbox, msg.uid);
      setComposeDraft(draft);
    } catch (err) {
      setComposeError(err instanceof Error ? err.message : 'Failed to prepare message');
    } finally {
      setComposeDraftLoading(false);
    }
  }, [selectedChannel, isAiChannel, ensureActionMessageLoaded, closeMoveDialog]);

  const handleMessageAction = useCallback(async (action: EmailMessageAction, msg: EmailMessageSummary) => {
    if (!selectedChannel) return;

    if (action === 'reply' || action === 'reply-all' || action === 'forward') {
      await loadComposeDraft(action, msg);
      return;
    }

    if (action === 'move') {
      closeCompose();
      setActionMessage(msg);
      setMoveOpen(true);
      setMoveError(null);
      if (isAiChannel) {
        setOriginMailboxesLoading(true);
        setOriginMailboxesError(null);
        try {
          const boxes = await aiApi.getOriginMailboxes(selectedChannel.id, msg.uid);
          setOriginMailboxes(boxes);
        } catch (err) {
          setOriginMailboxesError(err instanceof Error ? err.message : 'Failed to load folders');
          setOriginMailboxes([]);
        } finally {
          setOriginMailboxesLoading(false);
        }
      } else if (!(mailboxesByChannelId[selectedChannel.id]?.length)) {
        try {
          const boxes = await fetchMailboxes(selectedChannel.id);
          setMailboxesByChannelId(prev => ({ ...prev, [selectedChannel.id]: boxes }));
        } catch {
          // surfaced via mailboxesError
        }
      }
      return;
    }

    if (action === 'delete') {
      const trashName = isAiChannel ? 'trash' : (selectedChannel.trashMailbox?.trim() || 'TRASH');
      const confirmed = window.confirm(`Move this message to ${trashName}?`);
      if (!confirmed) return;

      try {
        if (isAiChannel) {
          await aiApi.deleteMessage(selectedChannel.id, msg.uid);
          removeAiMessageFromList(msg.mailbox, msg.uid);
          clearAiSelectedMessage();
          clearSelectedListMessage();
        } else {
          await emailApi.deleteMessage(selectedChannel.id, msg.mailbox, msg.uid);
          removeMessageFromList(msg.mailbox, msg.uid);
          clearSelectedMessage();
          clearSelectedListMessage();
        }
        closeCompose();
        closeMoveDialog();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete message';
        window.alert(message);
      }
      return;
    }

    if (action === 'mark-unread') {
      try {
        if (isAiChannel) {
          await aiApi.markUnread(selectedChannel.id, msg.uid);
          updateAiMessageReadState(msg.mailbox, msg.uid, false);
        } else {
          await emailApi.markUnread(selectedChannel.id, msg.mailbox, msg.uid);
          updateMessageReadState(msg.mailbox, msg.uid, false);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to mark message unread';
        window.alert(message);
      }
    }
  }, [
    selectedChannel,
    isAiChannel,
    loadComposeDraft,
    closeCompose,
    closeMoveDialog,
    mailboxesByChannelId,
    fetchMailboxes,
    removeMessageFromList,
    removeAiMessageFromList,
    clearSelectedMessage,
    clearAiSelectedMessage,
    clearSelectedListMessage,
    updateMessageReadState,
    updateAiMessageReadState
  ]);

  const handleSendCompose = useCallback(async (payload: { to: string; cc?: string; subject: string; text: string }) => {
    if (!selectedChannel || !activeSelectedMessage) {
      throw new Error('No message selected');
    }

    setComposeSending(true);
    setComposeError(null);
    try {
      const result = composeMode === 'forward'
        ? isAiChannel
          ? await aiApi.forward(
            selectedChannel.id,
            activeSelectedMessage.uid,
            { to: payload.to, subject: payload.subject, text: payload.text }
          )
          : await emailApi.forward(
            selectedChannel.id,
            activeSelectedMessage.mailbox,
            activeSelectedMessage.uid,
            { to: payload.to, subject: payload.subject, text: payload.text }
          )
        : isAiChannel
          ? await aiApi.reply(
            selectedChannel.id,
            activeSelectedMessage.uid,
            {
              to: payload.to,
              cc: payload.cc,
              subject: payload.subject,
              text: payload.text
            }
          )
          : await emailApi.reply(
            selectedChannel.id,
            activeSelectedMessage.mailbox,
            activeSelectedMessage.uid,
            {
              to: payload.to,
              cc: payload.cc,
              subject: payload.subject,
              text: payload.text
            }
          );
      closeCompose();
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send message';
      setComposeError(message);
      throw err;
    } finally {
      setComposeSending(false);
    }
  }, [selectedChannel, activeSelectedMessage, composeMode, isAiChannel, closeCompose]);

  const handleMoveMessage = useCallback(async (destination: string) => {
    if (!selectedChannel || !actionMessage) {
      throw new Error('No message selected');
    }

    if (isAiChannel) {
      await aiApi.moveMessage(selectedChannel.id, actionMessage.uid, destination);
      removeAiMessageFromList(actionMessage.mailbox, actionMessage.uid);
    } else {
      await emailApi.moveMessage(
        selectedChannel.id,
        actionMessage.mailbox,
        actionMessage.uid,
        destination
      );
      removeMessageFromList(actionMessage.mailbox, actionMessage.uid);
    }
    clearSelectedListMessage();
    closeMoveDialog();
    closeCompose();
  }, [selectedChannel, isAiChannel, actionMessage, removeMessageFromList, removeAiMessageFromList, closeMoveDialog, closeCompose, clearSelectedListMessage]);

  const renderMessagePanel = () => (
    <div className="app-content-full communications-content">
      <EmailMessageView
        message={activeSelectedMessage}
        loading={activeMessageLoading}
      />
      {composeOpen && activeSelectedMessage && (
        <EmailComposeComposer
          mode={composeMode}
          draft={composeDraft}
          loadingDraft={composeDraftLoading}
          sending={composeSending}
          error={composeError}
          onSend={handleSendCompose}
          onCancel={closeCompose}
        />
      )}
      {moveOpen && actionMessage && (
        <EmailMoveDialog
          mailboxes={isAiChannel ? originMailboxes : (mailboxesByChannelId[selectedChannel?.id ?? -1] ?? mailboxes)}
          currentMailbox={isAiChannel ? '' : actionMessage.mailbox}
          loading={isAiChannel ? originMailboxesLoading : mailboxesLoading}
          error={isAiChannel ? originMailboxesError : mailboxesError}
          onMove={handleMoveMessage}
          onCancel={closeMoveDialog}
        />
      )}
      <div className="email-messages-panel">
        <div className="email-messages-panel-header">
          <div className="email-messages-panel-title">
            <h3>{activeListLabel}</h3>
            <span className="email-messages-channel-label">{selectedChannel?.name}</span>
          </div>
          <button
            className="email-messages-sync-btn"
            onClick={handleSyncMessages}
            disabled={activeSyncing}
            title={isAiChannel ? 'Sync all channels and organize' : 'Sync from mailbox'}
          >
            {activeSyncing ? 'Syncing…' : '↺ Sync'}
          </button>
        </div>
        <EmailMessageList
          messages={activeMessages}
          loading={activeLoading}
          syncing={activeSyncing}
          error={activeMessagesError}
          selectedUid={selectedListMessage?.uid ?? null}
          selectedMailbox={selectedListMessage?.mailbox ?? null}
          onSelectMessage={handleSelectMessage}
          onMessageAction={handleMessageAction}
          emptyHint={isAiChannel ? 'No organized messages yet. Click Sync to pull from all channels.' : undefined}
        />
      </div>
    </div>
  );

  const renderContent = () => {
    if (contentView === 'add-channel') {
      return (
        <div className="app-content">
          <ChannelSettingsForm
            channel={null}
            oauthMessage={oauthMessage}
            onOauthMessageDismiss={() => setOauthMessage(null)}
            onSave={handleSaveChannel}
            onCancel={handleCancelChannelForm}
            onDirtyChange={onDirtyChange}
          />
        </div>
      );
    }

    if (contentView === 'edit-channel' && selectedChannel) {
      return (
        <div className="app-content">
          {selectedChannel.type === 'ai' ? (
            <AiChannelSettingsForm
              channel={selectedChannel}
              onSave={handleSaveChannel}
              onCancel={handleCancelChannelForm}
              onDirtyChange={onDirtyChange}
            />
          ) : (
            <ChannelSettingsForm
              channel={selectedChannel}
              oauthMessage={oauthMessage}
              onOauthMessageDismiss={() => setOauthMessage(null)}
              onSave={handleSaveChannel}
              onCancel={handleCancelChannelForm}
              onDirtyChange={onDirtyChange}
              onChannelRefresh={refreshSelectedChannel}
            />
          )}
        </div>
      );
    }

    if (contentView === 'channel' && selectedChannel) {
      if (selectedChannel.type === 'email' || selectedChannel.type === 'ai') {
        return renderMessagePanel();
      }

      return (
        <div className="app-content">
          <div className="communications-welcome">
            <h3>{selectedChannel.name}</h3>
            <p>Channel type "{selectedChannel.type}" is not yet supported for message viewing.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="app-content">
        <div className="app-welcome-message">
          <h2>Communications</h2>
          <p>Select a channel from the left to view messages, or add a new channel.</p>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="app-sidebar">
        <ChannelNav
          channels={channels}
          loading={channelsLoading}
          error={channelsError}
          selectedChannelId={selectedChannel?.id ?? null}
          expandedChannelIds={expandedChannelIds}
          onCollapseChannel={handleCollapseChannel}
          mailboxesByChannelId={mailboxesByChannelId}
          smartFoldersByChannelId={smartFoldersByChannelId}
          selectedMailbox={selectedMailbox}
          selectedFolder={selectedFolder}
          mailboxesLoading={mailboxesLoading}
          mailboxesError={mailboxesError}
          smartFoldersLoading={smartFoldersLoading}
          smartFoldersError={smartFoldersError}
          onSelectChannel={handleSelectChannel}
          onSelectChannelMailbox={handleSelectChannelMailbox}
          onSelectChannelFolder={handleSelectChannelFolder}
          onEditChannel={handleEditChannel}
          onDeleteChannel={handleDeleteChannel}
          onAddChannel={handleAddChannel}
        />
      </div>
      {renderContent()}
    </>
  );
};

export default CommunicationsApp;
